import { ComponentProps } from "react";
import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react";
import { Control, useController, FieldValues, Path } from "react-hook-form";
import styles from "./styles.module.scss";
import { getDataSuburbAutocomplete } from "../../NewEnquiryV3/EnquiryV3.api";

type OptionType = { label: string; key: string; postcode: string };

type Props<T extends FieldValues, Name extends Path<T>> = {
  inputProps: ComponentProps<"input">;
  label: string;
  helperText?: string;
  extraHelperText?: string;
  control: Control<T>;
  name: Name;
  [key: string]: any;
};

export const ControlledAutoComplete = <
  T extends FieldValues,
  Name extends Path<T>
>({
  inputProps,
  label,
  helperText,
  extraHelperText,
  control,
  name,
  ...props
}: Props<T, Name>) => {
  const { field, fieldState } = useController({
    name,
    control,
    disabled: inputProps.disabled,
  });
  const [showDropdown, setShowDropdown] = useState(false);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState(field.value?.label || "");
  const errorMessage = fieldState.error?.message;
  const isError = !!errorMessage;
  useEffect(() => {
    const handleSearch = async () => {
      setLoading(true);
      const data: any = await getDataSuburbAutocomplete(search);
      setLoading(false);
      setOptions(data);
    };
    const timerId = setTimeout(() => {
      handleSearch();
    }, 500);

    return () => {
      clearTimeout(timerId);
    };
  }, [search]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearch(value);
    if (value) {
      setShowDropdown(true);
    } else {
      setShowDropdown(false);
      field.onChange("");
    }
  };

  // Handle dropdown option selection
  const handleSelect = (option: OptionType) => {
    field.onChange(option);
    setSearch(option.label);
  };

  return (
    <div
      className={`${styles["form-control-enquiry"]} space-y-1 grid text-base text-[#3A3A3A]`}
    >
      <label htmlFor={name} className="font-medium">
        {label} <span className="text-[#EA4949]">*</span>
      </label>

      {/* Input */}
      <div className="relative">
        <input
          type="text"
          id={name}
          value={search}
          onChange={handleChange}
          autoComplete="suburb-select"
          className={`input-autofill text-[#484554] w-full border bg-white rounded-lg px-2 py-4 outline-none ${
            isError ? "border-[#EA4949]" : "border-[#E3E1DD]"
          }`}
          {...inputProps}
          onBlur={() => {
            //field.onBlur();
            setTimeout(() => {
              setShowDropdown(false);
              props.handleBlur("suburb");
            }, 150);
          }}
        />
        {/* Dropdown */}
        {showDropdown && (
          <ul className="absolute z-50 mt-1 w-full bg-white border border-[#D9D8DF] rounded-lg max-h-40 overflow-auto shadow-lg">
            {loading ? (
              <li className="px-4 py-2 text-gray-400">Loading...</li>
            ) : options.length > 0 ? (
              options.map((option, index) => {
                const displayLabel =
                  typeof option === "string" ? option : option.label;
                return (
                  <li
                    key={index}
                    onClick={() => handleSelect(option)}
                    className="text-[#484554] cursor-pointer px-4 py-2 hover:bg-gray-100"
                  >
                    {displayLabel}
                  </li>
                );
              })
            ) : (
              <li className="px-4 py-2 text-gray-400">No results found</li>
            )}
          </ul>
        )}
      </div>

      {/* Helper/Error Text */}

      {(isError || helperText || extraHelperText) && (
        <div className="flex items-center gap-x-4">
          {isError ? (
            <span className="flex-1 flex gap-x-2 items-center text-[#EA4949]">
              <Icon
                className="flex-shrink-0"
                icon="ph:warning-circle"
                width="16"
                height="16"
              />{" "}
              {errorMessage}
            </span>
          ) : (
            <span>{helperText}</span>
          )}
          {extraHelperText && <span>{extraHelperText}</span>}
        </div>
      )}
    </div>
  );
};
